import { useState } from "react";
import { Loader2, BookmarkPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Saves the WHOLE prescription being written (every item, guidance, formula or exam list) as a reusable model. */
export function SaveAsModelButton({
  onSave,
  mobile = false,
}: {
  /** Resolves when saved; throw/reject to keep the dialog open. */
  onSave: (title: string) => Promise<void>;
  mobile?: boolean;
}) {
  const { t } = useTranslation("prescriptions");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave(title.trim());
      setOpen(false);
      setTitle("");
    } catch {
      // The caller reports the error; keep the dialog open so nothing typed is lost.
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" className={mobile ? "h-12 w-full" : "self-start"} onClick={() => setOpen(true)}>
        <BookmarkPlus className="mr-2 h-4 w-4" aria-hidden="true" />
        {t("editor.saveModel.button")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("editor.saveModel.title")}</DialogTitle>
            <DialogDescription>{t("editor.saveModel.description")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="save-model-title" className="text-sm font-medium">{t("editor.saveModel.nameLabel")}</Label>
            <Input
              id="save-model-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder={t("editor.saveModel.namePlaceholder")}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("editor.shell.cancel")}</Button>
            <Button type="button" onClick={() => void submit()} disabled={!title.trim() || saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t("editor.saveModel.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
